/**
 * The kitchen's daily controls (ORDR-119, item 6, FR-CFM-005).
 *
 * SMALL ON PURPOSE: a kitchen screen, whatever display is enrolled. Two
 * values, each gated exactly as the full surface gates it (ruling B10):
 *
 * - the CAPACITY coefficient, `capacity:manage`;
 * - the declared kitchen delay, PREP_TIMES `delay_minutes`,
 *   `prep-time:manage`. HQ ruled (C-HQ3) that this key IS FR-ORD-020's
 *   temporary delay; paio-ordering's `services/injection.py` reads it as "a
 *   location that declares a delay".
 *
 * Each write still sends the WHOLE domain object: the other keys ride along
 * unchanged, because the server REPLACES this node's own value.
 *
 * `scope.nodeId` IS USED AS GIVEN, never another id guessed from
 * `scope.siteId`. A 404 is the merged GET's answer for a node outside the
 * caller's scope, with the same body as one that does not exist (ORDR-135,
 * HQ ruling C-HQ6), so the panel says "not available at this scope" and
 * offers nothing to retry.
 */

import { useEffect, type ReactElement } from "react";
import type { DailyControlsProps } from "./index.js";
import { GroupCard } from "./GroupCard.js";
import { useGroups } from "./useGroups.js";
import type { ConfigDomain } from "./domains.js";

const DAILY: readonly { readonly domain: ConfigDomain; readonly label: string; readonly key: string }[] = [
  { domain: "CAPACITY", label: "Capacity", key: "capacity_coefficient_permille" },
  { domain: "PREP_TIMES", label: "Declared kitchen delay (minutes)", key: "delay_minutes" },
];

export function DailyControlsView({ client, scope }: DailyControlsProps): ReactElement {
  const groups = useGroups(client, scope.nodeId);
  const { load } = groups;

  useEffect(() => {
    load(DAILY.map((d) => d.domain));
  }, [load]);

  const outOfScope = DAILY.some((d) => {
    const state = groups.states[d.domain];
    return state?.status === "failed" && state.failure.status === 404;
  });

  return (
    <section data-testid="daily-controls" className="flex flex-col gap-2 text-sm">
      <h2 className="font-medium">Today at {scope.nodeName}</h2>
      {outOfScope ? (
        <p>Daily controls are not available at this scope.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {DAILY.map(({ domain, label, key }) => (
            <GroupCard
              key={domain}
              testId={`group-daily-${domain}`}
              label={label}
              domain={domain}
              state={groups.states[domain]}
              scope={scope}
              api={groups.api}
              keys={(k) => k === key}
              typedOnly
              onRetry={() => groups.reload(domain)}
              onSaved={(reading) => groups.adopt(domain, reading)}
            />
          ))}
        </ul>
      )}
      <ul data-testid="daily-not-here" className="list-disc pl-5 text-xs">
        <li>86 an item: not here yet; commerce owns it (menu:set-86-state).</li>
        <li>Item availability: not here yet; commerce owns it.</li>
        <li>Menu schedule: not here yet; nothing implements it yet.</li>
      </ul>
    </section>
  );
}
